import { store } from '@/app/store';
import {
  custodyReportsApi,
  selectAllCustodyReports,
  useGenerateCustodyReportsMutation,
  useGetCustodyReportsQuery
} from '@/features/custodyReports/custodyReportsApi';
import LoadingSpinner from '@/shared/components/LoadingSpinner/LoadingSpinner';
import Report from '@/shared/components/Report/Report';
import SettingsViewWrapper from '@/shared/components/SettingsViewWrapper/SettingsViewWrapper';
import { useWindowDimensions } from '@/shared/hooks/useWindowDimension';
import { IonButton, IonCol, IonRow, IonText, useIonToast } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styles from './CustodReports.module.css';

const CustodyReports = () => {
  const [lastReportId, setLastReportId] = useState<number | null>(null);
  const [highlightReportId, setHighlightReportId] = useState<number | null>(null);
  const { isMobileView } = useWindowDimensions();
  const { t } = useTranslation();
  const [presentToast] = useIonToast();
  const dispatch = useDispatch();

  const { isLoading: isCustodyReportsLoading } = useGetCustodyReportsQuery(undefined);

  const { isSuccess: isGetCustodyReportsSuccess } = useGetCustodyReportsQuery(undefined);

  const allCustodyReports = selectAllCustodyReports(store.getState());

  const [generateNewCustodyReport, {}] = useGenerateCustodyReportsMutation();

  const refetchCustodyReports = useCallback(() => {
    dispatch(custodyReportsApi.util.invalidateTags(['CustodyReports']));
  }, [dispatch]);

  const handleGenerateCustodyReport = useCallback(async () => {
    try {
      await generateNewCustodyReport().unwrap();
      presentToast({
        message: t('settings.custody_reports.generate.success'),
        duration: 2000,
        position: 'top',
        color: 'success'
      });
      refetchCustodyReports();
    } catch (error) {
      console.error('Failed to generate custody reports', error);
      presentToast({
        message: t('settings.custody_reports.generate.fail'),
        duration: 2000,
        position: 'top',
        color: 'danger'
      });
    }
  }, [generateNewCustodyReport, presentToast, refetchCustodyReports, t]);

  useEffect(() => {
    if (allCustodyReports && allCustodyReports.length > 0) {
      if (lastReportId === null) {
        setLastReportId(allCustodyReports[0].id);
      } else {
        const newFirstReport = allCustodyReports[0];
        if (newFirstReport.id !== lastReportId) {
          setHighlightReportId(newFirstReport.id);
          setLastReportId(newFirstReport.id);
        }
      }
    }
  }, [allCustodyReports, lastReportId]);

  return (
    <SettingsViewWrapper name={t('settings.custody_reports')}>
      <>
        {isCustodyReportsLoading && <LoadingSpinner />}
        {!isMobileView && <h1>{t('settings.custody_reports')}</h1>}

        {
          <IonRow className={styles.generateButtonContainer}>
            <IonCol>
              <IonButton expand={isMobileView ? 'block' : undefined} onClick={handleGenerateCustodyReport}>
                <IonText className={styles.generateText}>{t('settings.custody_reports.generate')}</IonText>
              </IonButton>
            </IonCol>
          </IonRow>
        }
        {allCustodyReports &&
          allCustodyReports.length > 0 &&
          isGetCustodyReportsSuccess &&
          allCustodyReports.map((report) => (
            <Report key={report.id} report={report} highlightReportId={highlightReportId ? highlightReportId : null} />
          ))}
      </>
    </SettingsViewWrapper>
  );
};

export default CustodyReports;
